import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, EyeOff, Stethoscope } from 'lucide-react';
import { useAuth } from '../utils/auth';
import WorkspaceFrame from '../components/WorkspaceFrame';
import './AuthPages.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const redirectTo = location.state?.from?.pathname || '/doctor';

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(username, password);
      navigate(redirectTo, { replace: true });
    } catch (requestError) {
      console.error('登录失败:', requestError);
      setError(
        `${t('auth.loginFailed')}: ${requestError.response?.data?.error || t('auth.invalidCredentials')}`
      );
    } finally {
      setLoading(false);
    }
  };

  const checkingAccess = authLoading || isAuthenticated;
  const passwordToggleLabel = showPassword ? t('auth.hidePassword') : t('auth.showPassword');
  const PasswordVisibilityIcon = showPassword ? EyeOff : Eye;

  return (
    <WorkspaceFrame className="workspace-frame--auth">
      <main className="auth-page auth-page--doctor" aria-labelledby="doctor-login-title">
        <Link to="/" className="auth-page__back">
          <ArrowLeft className="auth-page__back-icon" size={18} aria-hidden="true" />
          <span>{t('auth.backToWorkspaces')}</span>
        </Link>

        <section className="auth-page__surface">
          <div className="auth-page__role">
            <span className="auth-page__role-icon" aria-hidden="true">
              <Stethoscope size={24} strokeWidth={1.8} />
            </span>
            <span className="auth-page__role-label">{t('dashboard.doctorDesk')}</span>
          </div>

          <header className="auth-page__header">
            <h1 id="doctor-login-title" className="auth-page__title">{t('auth.loginTitle')}</h1>
            <p className="auth-page__description">{t('auth.doctorLoginDescription')}</p>
          </header>

          <div className="auth-page__content">
            {checkingAccess ? (
              <div className="auth-page__loading" role="status" aria-live="polite">
                <div className="loading-orb" aria-hidden="true" />
                <div className="auth-page__loading-copy">
                  <span className="auth-page__loading-title">{t('auth.checkingLoginStatus')}</span>
                  <p className="auth-page__loading-description">{t('auth.checkingLoginDescription')}</p>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div className="status-banner status-banner--error" role="alert">
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="auth-page__form stack" aria-busy={loading || undefined}>
                  <div className="field">
                    <label htmlFor="doctor-username" className="field__label">
                      {t('auth.username')}
                    </label>
                    <input
                      id="doctor-username"
                      type="text"
                      className="theme-input"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder={t('auth.usernamePlaceholder')}
                      autoComplete="username"
                      required
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="doctor-password" className="field__label">
                      {t('auth.password')}
                    </label>
                    <div className="auth-secret-field">
                      <input
                        id="doctor-password"
                        type={showPassword ? 'text' : 'password'}
                        className="theme-input auth-secret-field__input"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder={t('auth.passwordPlaceholder')}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="auth-secret-field__toggle"
                        onClick={() => setShowPassword((visible) => !visible)}
                        aria-label={passwordToggleLabel}
                        aria-pressed={showPassword}
                        title={passwordToggleLabel}
                      >
                        <PasswordVisibilityIcon size={19} strokeWidth={2} aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="auth-page__actions">
                    <button
                      type="submit"
                      className="theme-button theme-button--primary theme-button--full"
                      disabled={loading}
                    >
                      {loading ? t('auth.loggingIn') : t('auth.login')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </section>
      </main>
    </WorkspaceFrame>
  );
};

export default Login;
